// /groups/:groupId — the group's HOME, which is its feed, live.
//
// A mixed stream of three things: member posts, the lifecycle messages the app
// writes on the group's behalf ("Game on!", results announcements), and an
// announcement card for every event, with inline RSVP so answering never means
// leaving the stream. See docs/plan-feed-as-home.md.
//
// The event card is rendered by the SAME eventCard() the events tab uses, off the
// SAME EventSummary. Nothing about an event is re-rendered from the post body,
// which holds only the organizer's announcement line — so editing an event
// updates its card here instead of leaving a stale time in the scroll.
//
// Newest-first, unlike the chat thread, because a feed is read downward from the
// most recent.

import {
  api,
  ApiClientError,
  deleteFeedPost,
  fetchFeed,
  fetchGroup,
  fetchPot,
  listEvents,
  postChatMessage,
  postToFeed,
  putReaction,
  removeReaction,
} from '../api/client.js';
import { getCurrentUserId } from '../chrome.js';
import { navigate, onLeave, type View } from '../router.js';
import { el } from '../dom.js';
import {
  fmtInstant,
  fmtRelative,
  groupChannelName,
  GroupRole,
  TimeFormat,
  type EventSummary,
  type FeedPage,
  type ReactionRoster,
  type RsvpCounts,
  type FeedPost,
  type GroupDetail,
  type GroupPot,
} from '@msc/shared';
import { subscribe } from '../realtime.js';
import { viewerZone } from '../viewerZone.js';
import { applyCountsToSummary, eventCard } from './eventCard.js';
import { groupRunsFasts, groupShowsLeaderboard, groupSubnav } from './groupNav.js';
import { pinnableEvents, stickyPanel } from './stickyPanel.js';
import { blockButton, reportButton } from './reportControl.js';
import { reactionBar, type ReactionApi } from './reactionBar.js';
import { commentThread, type CommentHost } from './postComments.js';
import { groupPotCard } from './groupPot.js';

export const feedView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  let group: GroupDetail;
  let page: FeedPage;
  let zone: string;
  let upcoming: EventSummary[];
  let viewerId: number | null;
  let pot: GroupPot | null;
  try {
    [group, page, zone, upcoming, viewerId, pot] = await Promise.all([
      fetchGroup(groupId),
      fetchFeed(groupId),
      viewerZone(),
      // For the sticky panel. Open events only — a closed one is not something
      // to pin an RSVP control to.
      listEvents(groupId, { upcoming: true, includeClosed: false }),
      getCurrentUserId(),
      // Best-effort: a failed pot read must never stop the feed rendering.
      fetchPot(groupId).catch(() => null),
    ]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load the feed.'));
    return;
  }

  const list = el('div', { class: 'feed-list' });
  const seen = new Map<number, FeedPost>();

  function render(post: FeedPost, atTop = false): void {
    if (seen.has(post.postId)) return;
    seen.set(post.postId, post);
    const node = postCard(
      post,
      zone,
      viewerId,
      () => {
        node.remove();
        seen.delete(post.postId);
      },
      group.isSuspended,
    );
    if (atTop) list.prepend(node);
    else list.append(node);
  }

  /** Apply a live reaction update to the post already on screen. */
  /**
   * The comment thread element inside one post's card, if that card is on
   * screen and is a plain post.
   *
   * Queried from the DOM rather than held in a map, for the reason the
   * reaction bar already documents: a card can be replaced by a re-render, and
   * a stored handle would then point at a detached node that accepts writes
   * nobody ever sees.
   */
  function commentHostFor(postId: number): CommentHost | null {
    return (
      list.querySelector<CommentHost>(`[data-post="${postId}"] .comments`) ?? null
    );
  }

  function applyReactionUpdate(postId: number, reactions: ReactionRoster): void {
    const post = seen.get(postId);
    if (!post) return;
    post.reactions = reactions;
    const bar = list.querySelector(`[data-post="${postId}"] .reaction-bar`);
    bar?.replaceWith(reactionBar(post, viewerId, feedReactionApi(post.postId)));
  }

  /**
   * Rebuild the feed card for one event after it was answered somewhere else.
   *
   * The PHP version did this through an `mscRefreshEventCard` hook. Rebuilding
   * the whole post node is fine and cheap: at most one post per event carries a
   * card ("one card per event, ever" — docs/plan-feed-as-home.md).
   */
  function refreshEventCards(eventId: number, counts: RsvpCounts): void {
    for (const post of seen.values()) {
      if (post.event?.eventId !== eventId) continue;
      applyCountsToSummary(post.event, counts);
      const old = list.querySelector(`[data-post="${post.postId}"]`);
      if (!old) continue;
      const fresh = postCard(
        post,
        zone,
        viewerId,
        () => {
          fresh.remove();
          seen.delete(post.postId);
        },
        group.isSuspended,
      );
      old.replaceWith(fresh);
    }
  }

  for (const p of page.posts) render(p);

  /**
   * Tell the server how far this member has now read, so the header bell can
   * stop counting these.
   *
   * ⚠ THE HIGHEST ID WE ACTUALLY RENDERED, not "the newest post". The server
   * could work the latter out for itself, and that is exactly the bug: a post
   * arriving between the query and the paint would be marked read while never
   * having been on screen, and it would never be counted again.
   *
   * ⚠ Recomputed from `seen` rather than from `page.posts`, because a realtime
   * `feed.post` frame can add a NEWER post after this first paint. Reading the
   * map is what keeps the cursor honest as the page lives.
   *
   * Fire-and-forget: a failed mark just means the badge stays up until the
   * next visit, which is the safe direction. It must never surface an error
   * over the feed the member came to read.
   */
  const markRead = (): void => {
    let highest = 0;
    for (const id of seen.keys()) if (id > highest) highest = id;
    if (highest <= 0) return;
    void api(`/groups/${groupId}/feed-read`, {
      method: 'POST',
      body: { postId: highest },
    }).catch(() => {});
  };
  markRead();

  let oldest = page.oldestId;
  const olderBtn = el('button', { class: 'btn btn-link btn-standalone', type: 'button' }, 'Load older posts');
  const olderWrap = el('div', {}, page.hasMore ? [olderBtn] : []);

  olderBtn.addEventListener('click', async () => {
    if (oldest === null) return;
    olderBtn.setAttribute('disabled', 'disabled');
    try {
      const older = await fetchFeed(groupId, oldest);
      for (const p of older.posts) render(p);
      oldest = older.oldestId;
      if (!older.hasMore) olderWrap.replaceChildren();
    } catch {
      olderBtn.textContent = 'Could not load older posts';
    } finally {
      olderBtn.removeAttribute('disabled');
    }
  });

  // Live. Released on navigation via onLeave — the socket itself is a
  // session-long singleton and survives leaving and coming back.
  const release = subscribe(groupChannelName(groupId), (frame) => {
    if (frame.event === 'feed.post') {
      render(frame.data, true);
    } else if (frame.event === 'feed.deleted') {
      list.querySelector(`[data-post="${frame.data.postId}"]`)?.remove();
      seen.delete(frame.data.postId);
    } else if (frame.event === 'feed.reaction') {
      applyReactionUpdate(frame.data.postId, frame.data.reactions);
    } else if (frame.event === 'feed.comment') {
      // ⚠ Hand it to the card's own thread rather than re-rendering the post.
      // Re-rendering would replace the node the reader may have open and
      // scrolled into, and would throw away their half-typed comment -- the
      // same class of mistake as a reaction bar that replaces itself.
      commentHostFor(frame.data.comment.postId)?.__acceptComment?.(frame.data.comment);
    } else if (frame.event === 'feed.comment.deleted') {
      commentHostFor(frame.data.postId)?.__removeComment?.(frame.data.commentId);
    }
  });
  onLeave(release);

  const header = el('div', { class: 'page-head' }, [
    el('h1', {}, group.name),
    el('p', { class: 'muted' }, `You are ${group.myRoleLabel} · ${group.memberCount} members`),
  ]);

  // The DAY-OF box, and only that. Until 2026-09-10 this was the full upcoming
  // picker, which duplicated every event already on the page as a card — two
  // RSVP controls for one event on one screen, the exact thing `home.ts` says
  // is worse than either alone. Will, 2026-09-10: "on the group feed, you just
  // see each event for the group. I don't think we do separate boxes. This is
  // really for the main feed."
  //
  // ⚠ It KEEPS a day-of box, deliberately, and that knowingly keeps ONE narrow
  // case of the duplication: on the day of an event this box and that event's
  // announcement card are both on screen, each with an RSVP control. Do not
  // "fix" it later. The card announces and takes an advance RSVP; the box is the
  // day-of confirmation surface, which is the product's differentiator and must
  // not be scrollable-past. Decided with Will 2026-09-10, docs/plan-next-up.md §3e.
  //
  // Suppressed entirely for a suspended group, where every write control on the
  // page is going to be refused anyway.
  const pinned = group.isSuspended ? [] : pinnableEvents(upcoming);
  const panel = stickyPanel({
    events: pinned,
    viewerZone: zone,
    dayOfOnly: true,
    // One group, so naming it on every row would be noise.
    // The same event is also a card further down the stream, and answering in
    // the box has to show there too.
    onRsvpChange: refreshEventCards,
  });

  main.replaceChildren(
    el('p', {}, [el('a', { href: '/groups' }, 'Back to your groups')]),
    header,
    // Above the content, not below it. This is the group's landing page now, so
    // it is where a member first learns the group is suspended — and every write
    // control on this page will be refused by the server.
    ...(group.isSuspended
      ? [
          el(
            'p',
            { class: 'notice notice-suspended' },
            'This group is suspended by a site administrator. You can still read ' +
              'everything here, but nobody can post, upload, RSVP, or create events ' +
              'until it is restored.',
          ),
        ]
      : []),
    groupSubnav(groupId, 'feed', group.myRole >= GroupRole.ADMIN, groupRunsFasts(group.allowedEventTypes), groupShowsLeaderboard(group.allowedEventTypes)),
    panel.root,
    ...(pot?.enabled ? [groupPotCard(pot, groupId, zone)] : []),
    composer(groupId, (p) => render(p, true)),
    list,
    olderWrap,
  );
};

export function postCard(
  post: FeedPost,
  zone: string,
  viewerId: number | null,
  onRemoved: () => void,
  suspended: boolean,
): HTMLElement {
  // Avatar, name, relative time — the shape the PHP feed had. A feed is read as a
  // stream of recent things, so "5m ago" beats "Aug 21, 2026 7:59 PM PDT"; the
  // absolute instant is on the title attribute for anyone who wants it.
  const stamp = el('span', { class: 'muted feed-when' }, fmtRelative(post.createdAt, zone, Date.now()));
  stamp.setAttribute('title', fmtInstant(post.createdAt, zone, TimeFormat.INSTANT));

  const meta = el('div', { class: 'feed-meta' }, [
    avatar(post.displayName),
    el('strong', {}, post.displayName),
    stamp,
  ]);

  if (post.canDelete) {
    const btn = el('button', { class: 'btn btn-link btn-danger', type: 'button' }, 'Delete');
    btn.addEventListener('click', async () => {
      if (!window.confirm('Delete this post?')) return;
      btn.setAttribute('disabled', 'disabled');
      try {
        await deleteFeedPost(post.postId);
        onRemoved();
      } catch (err) {
        window.alert(err instanceof ApiClientError ? err.message : 'Could not delete that.');
        btn.removeAttribute('disabled');
      }
    });
    meta.append(' ', btn);
  }

  // Guideline 1.2. Never on your own post; `mine` is false on an event card, so
  // announcements are reportable too, which is right -- the body is authored.
  const report = reportButton('post', post.postId, post.mine);
  if (report) meta.append(' ', report);
  // Not on an event card: a block must never hide the group's schedule.
  const block = post.event === null ? blockButton(post.userId, post.displayName, post.mine) : null;
  if (block) meta.append(' ', block);

  const children: HTMLElement[] = [meta];
  if (post.body) children.push(el('p', {}, post.body));
  if (post.photoUrl) {
    children.push(el('img', { class: 'feed-photo', src: post.photoUrl, alt: '', loading: 'lazy' }));
  }

  if (post.event) {
    // The whole event, live, with inline RSVP — the same card the events tab
    // renders. `rsvp: true` is the point of putting it here: a member answers
    // without leaving the stream. A closed event's card stays and eventCard()
    // already renders its Closed/Canceled badge and freezes the control.
    children.push(
      eventCard(post.event, zone, {
        rsvp: true,
        // The panel above is suppressed for a suspended group; the cards in the
        // stream have to be too, or they offer buttons the server refuses.
        ...(suspended ? { frozenNote: 'RSVPs are paused while this group is suspended.' } : {}),
      }),
    );
    // ONE card per conversation: a one-line summary of the event's conversation
    // under the event, never a row per message. The server already withheld a
    // blocked member's words from the count and the preview.
    const chat = post.chat;
    children.push(
      el('p', { class: 'small feed-chat-summary' }, [
        el(
          'a',
          { href: `/events/${post.event.eventId}/chat` },
          chat
            ? `${chat.count} message${chat.count === 1 ? '' : 's'}${
                chat.unread > 0 ? `, ${chat.unread} new` : ''
              }`
            : 'Start the conversation',
        ),
        ...(chat ? [el('span', { class: 'muted' }, ` · ${chat.latestAuthor}: ${chat.latestExcerpt}`)] : []),
      ]),
    );
  } else if (post.eventId !== null) {
    // Not a card — a lifecycle message ("Game on!") that merely references an
    // event. Its body is the content; the link is worth following.
    children.push(
      el('p', { class: 'small' }, [el('a', { href: `/events/${post.eventId}` }, 'Open the event')]),
    );
  }

  // Any post can carry reactions, event cards included.
  children.push(reactionBar(post, viewerId, feedReactionApi(post.postId)));

  // ⚠ COMMENTS ON PLAIN POSTS ONLY, and `post.event` is the wrong test -- a
  // lifecycle message ("Game on!") carries an eventId with no event attached,
  // and an announcement card whose event was deleted also renders with
  // `event: null`. `eventId === null` is the one that means "an ordinary post
  // somebody wrote". Getting this wrong would put a composer on an event,
  // which is the exact gate the server refuses.
  if (post.eventId === null) {
    children.push(commentThread(post, zone));
  }

  return el(
    'article',
    {
      class: post.event ? 'card feed-item feed-item-event' : 'card feed-item',
      'data-post': post.postId,
      // Untagged on an event card, so removeAuthoredBy() never takes one.
      'data-author': post.event ? undefined : post.userId,
    },
    children,
  );
}

/** The feed's endpoints, for the shared reaction bar. */
function feedReactionApi(postId: number): ReactionApi {
  return {
    put: async (kind) => (await putReaction(postId, kind)).reactions,
    remove: async () => (await removeReaction(postId)).reactions,
  };
}

/**
 * Initials in a circle.
 *
 * The PHP version took the first two CHARACTERS of the display name, which gives
 * "Wi" for "Will C.". Initials of the first two words give "WC", which is what
 * an avatar is conventionally read as — a deliberate improvement on the original
 * rather than a straight port. Falls back to the first two characters when the
 * name is a single word.
 *
 * `aria-hidden`, because the name it abbreviates is the very next element, and a
 * screen reader announcing "WC, Will Childers" is worse than useless.
 */
function avatar(displayName: string): HTMLElement {
  const initials = displayName
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('');
  return el(
    'span',
    { class: 'feed-avatar', 'aria-hidden': 'true' },
    initials || displayName.slice(0, 2),
  );
}

function composer(groupId: number, onPosted: (p: FeedPost) => void): HTMLElement {
  // Audience: the whole group's feed, or one event's conversation. The choice is
  // who you are talking to; only the notifying differs (see
  // docs/plan-event-conversation.md).
  const error = el('p', { class: 'form-error small', hidden: true });
  const input = el('textarea', {
    id: 'feed-body',
    class: 'composer-input',
    rows: 1,
    placeholder: 'Post to the group',
    maxlength: 4000,
  }) as HTMLTextAreaElement;
  const submit = el('button', { type: 'submit', class: 'btn btn-sm' }, 'Post');
  const audience = el('select', { id: 'feed-audience', 'aria-label': 'Who is this for' }, [
    el('option', { value: '' }, 'Whole group'),
  ]) as HTMLSelectElement;
  const where = el('p', { class: 'muted small composer-hint', hidden: true });

  // Loaded once, in the background: the composer works as "Whole group" before
  // (or without) this arriving. Open events only, soonest first.
  void listEvents(groupId, { includeClosed: false, limit: 20 })
    .then((events) => {
      for (const e of events) {
        audience.append(el('option', { value: String(e.eventId) }, `This event: ${e.title}`));
      }
    })
    .catch(() => undefined);

  // Grows with the text instead of reserving two rows up front. This sits at the
  // top of the group's landing page, so its resting height is what a member sees
  // before anything else.
  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = `${input.scrollHeight}px`;
  });

  const form = el('form', { method: 'post', action: `/groups/${groupId}/posts` }, [
    el('div', { class: 'composer-row' }, [input, submit]),
    audience,
    where,
    el(
      'p',
      { class: 'muted small composer-hint' },
      'Photos go on the Photos page. A caption there shows in the gallery too.',
    ),
    error,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const body = input.value.trim();
    if (body === '') return;

    error.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    input.value = '';
    input.style.height = '';
    try {
      const eventId = audience.value === '' ? null : Number(audience.value);
      if (eventId === null) {
        where.hidden = true;
        onPosted(await postToFeed(groupId, body));
      } else {
        // An event conversation message is not a feed post, so there is nothing
        // to render here; say where it went and link to it.
        await postChatMessage(eventId, body);
        where.replaceChildren(
          'Posted to the conversation. ',
          el('a', { href: `/events/${eventId}/chat` }, 'Open it'),
        );
        where.hidden = false;
      }
    } catch (err) {
      input.value = body; // Nothing typed is ever lost.
      input.style.height = 'auto';
      input.style.height = `${input.scrollHeight}px`;
      error.textContent = err instanceof ApiClientError ? err.message : 'Could not post that.';
      error.hidden = false;
    } finally {
      submit.removeAttribute('disabled');
    }
  });

  return el('section', { class: 'card' }, [form]);
}
